import chunk from "lodash/chunk";
import { runInAction } from "mobx";
import * as Sentry from "@sentry/react";
import JulgamentoApi from "../api/JulgamentoApi";
import MinutaApi from "../api/MinutaApi";
import { Processo, TipoMinuta } from "../modelo";
import { SituacaoMinuta } from "../modelo/SituacaoMinuta";
import ProcessoTipoJulgamento, { TipoJulgamentoEnum } from "../modelo/TipoJulgamento";
import UsuarioStore from '../store/UsuarioStore';
import MensagemStore from "../store/MensagemStore";
import ProcessoStore from "../store/ProcessoStore";
import ControleGabineteService from "./ControleGabineteService";
import EscaninhoStore from "@/store/EscaninhoStore";
import { apresentarApiErroPadrao, extrairApiErroPadrao } from '../utils/ApiErroPadraoUtils';
import { IProgressoOperacaoLote, IResultadoOperacaoLote } from "@/modelo/ErroOperacaoLote";

const tarefasParaVistoJaRealizado = ['para prosseguir no julgamento', 'para inclusão em pauta', 'assinar acórdão', 'aguardando inclusão em pauta', 'aguardando inclusão em pauta ou sessão', 'aguardando sessão de julgamento',
    'aguardando pauta', 'incluído em pauta o processo para julgamento', 'para inclusão em pauta', 'processo em pauta - concluso ao relator'
];

class MovimentacaoInternaService {

    async enviarParaPauta(
        tipoJulgamento: string,
        processos: Processo[],
        aoAtualizarProgresso?: (progresso: IProgressoOperacaoLote) => void,
        deveInterromperAposAtual?: () => boolean
    ): Promise<IResultadoOperacaoLote> {
        if (UsuarioStore.ehMagistrado) {
            const sucesso = [];
            const erro = [];
            const total = processos.length;
            let processados = 0;
            let interrompido = false;

            const publicarProgresso = () => {
                if (!aoAtualizarProgresso) {
                    return;
                }
                aoAtualizarProgresso({
                    total,
                    processados,
                    sucessos: sucesso.length,
                    erros: erro.length
                });
            };

            publicarProgresso();

            for (const processo of processos) {
                if (deveInterromperAposAtual?.()) {
                    interrompido = true;
                    break;
                }

                try {
                    const resultado = await ControleGabineteService.enviarParaPauta(processo.identificadorProcesso, UsuarioStore.gabinete, true);
                    sucesso.push({ idProcesso: processo.identificadorProcesso, resultadoOperacao: resultado });
                } catch (error) {
                    const apiErroPadrao = extrairApiErroPadrao(error);
                    erro.push({
                        idProcesso: processo.identificadorProcesso,
                        numeroProcesso: processo.numeroProcesso,
                        message: apiErroPadrao?.message || 'Erro inesperado no servidor',
                        detail: apiErroPadrao?.details || error?.response?.data?.details,
                        restId: apiErroPadrao?.requestId || error?.response?.data?.requestId
                    });
                }

                processados++;
                publicarProgresso();
            };

            const totalConcluidos = sucesso.length + erro.length;
            if (processados !== totalConcluidos) {
                processados = totalConcluidos;
                publicarProgresso();
            }


            if (interrompido) {
                const textoConcluidos = processados === 1 ? '1 processo concluído' : `${processados} processos concluídos`;
                const textoErros = erro.length === 1 ? '1 com erro' : `${erro.length} com erro`;
                MensagemStore.apresentarAviso(`Envio para pauta interrompido. ${textoConcluidos}, sendo ${textoErros}.`);
            } else if (erro.length > 0) {
                const textoSucessos = sucesso.length === 1 ? '1 processo enviado' : `${sucesso.length} processos enviados`;
                const textoErros = erro.length === 1 ? '1 com erro' : `${erro.length} com erro`;
                const textoResumo = sucesso.length > 0
                    ? `Operação finalizada com falhas: ${textoSucessos} e ${textoErros}.`
                    : `Erro ao enviar processos para pauta. ${erro.length === 1 ? '1 processo com erro' : `${erro.length} processos com erro`}.`;
                MensagemStore.apresentarAviso(textoResumo);
            } else {
                MensagemStore.apresentarMensagem('Operação realizada com sucesso.');
            }

            return {
                quantidadeSucesso: sucesso.length,
                quantidadeErro: erro.length,
                erros: erro,
                interrompido
            };
        } else {
            MensagemStore.apresentarAviso(`Apenas o magistrado pode enviar para a pauta`);
            return {
                quantidadeSucesso: 0,
                quantidadeErro: 0,
                erros: [],
                interrompido: false
            };
        }
    }

    async enviarAptoJulgamento(): Promise<boolean> {
        if (UsuarioStore.ehMagistrado) {
            const sucesso = [];
            const erro = [];
            const promises = EscaninhoStore.processosSelecionados.map((processo) => {
                return ControleGabineteService.enviarAptoJulgamento(processo.identificadorProcesso, UsuarioStore.gabinete).then((result) => {
                    sucesso.push({ idProcesso: processo.identificadorProcesso, resultadoOperacao: result });
                }, (error) => {
                    erro.push({
                        idProcesso: processo.identificadorProcesso,
                        resultadoOperacao: error,
                        processo: processo
                    });
                });
            });
            Promise.all(promises).then(() => {
                const mensagemSucesso = 'Operação realizada com sucesso';
                const mensagemErroParcial = 'Operação não realiza para alguns processos';
                const mensagemErroTotal = 'Erro ao enviar processos aptos para sessão';

                let mensagem = erro.length <= 0 ? mensagemSucesso : sucesso.length <= 0 ? mensagemErroTotal : mensagemErroParcial;

                for (const i in erro) {
                    if (erro[i].resultadoOperacao?.toString().includes('Resposta de erro ao gravar a minuta no PJE')) {
                        mensagem += ' - Ocorreu um erro ao gravar a minuta no PJe no processo ' + erro[i].processo.numeroProcesso + '. Verifique a formação da minuta, pois é uma provável causa.';
                    }
                }

                if (erro.length > 0) {
                    MensagemStore.apresentarErro(mensagem);
                } else {
                    MensagemStore.apresentarMensagem(mensagem);
                }
            });
            return true;
        } else {
            MensagemStore.apresentarAviso(`Apenas o magistrado pode enviar para a pauta`);
            return false;
        }
    }

    async indicarModalidadeJulgamentoVirtual(identificadoresProcessos: number[], votacaoVirtual: TipoJulgamentoEnum) {
        if (identificadoresProcessos.length > 0) {
            return await JulgamentoApi.indicarModalidadeJulgamentoVirtual(identificadoresProcessos, votacaoVirtual);
        }
    }

    async carregarTipoJulgamento(idProcessos?: number[], sessaoFutura?: boolean) {
        runInAction(() => {
            ProcessoStore.carregandoTipoJulgamento = true;
        });
        if (!idProcessos || idProcessos.length === 0) {
            idProcessos = ProcessoStore._processos.filter(proc => proc.minutas.filter(min => min.situacaoMinuta.codigo === SituacaoMinuta.LIBERADA_PARA_ASSINATURA_OU_INCLUSAO_EM_PAUTA
                && min.tipo.codigo === TipoMinuta.VOTO).length > 0)
                .map(proc => proc.identificadorProcesso);
        }
        const listaIdsProcesso = chunk(idProcessos, 900);
        const promises = listaIdsProcesso.map(ids => JulgamentoApi.buscarTipoJulgamento(ids, sessaoFutura));
        try {
            const result = await Promise.all(promises);
            const tiposJulgamento = [];
            result.forEach(result1 => result1.forEach(r => tiposJulgamento[r.idProcesso] = r));
            ProcessoStore._processos.filter(proc => idProcessos.includes(proc.identificadorProcesso)).forEach(proc => {
                runInAction(() => {
                    let tpJulg = tiposJulgamento[proc.identificadorProcesso];
                    if (!tpJulg) {
                        tpJulg = new ProcessoTipoJulgamento();
                        tpJulg.tipoJulgamento = TipoJulgamentoEnum.PRESENCIAL;
                    }
                    proc.tipoJulgamento = tpJulg;
                });
            });
        } catch (erro) {
            // Carregamento best-effort da hidratação da store: uma falha (ex.: 500 no
            // serviço de votação virtual) não deve virar unhandled rejection nem
            // interromper o carregamento do escaninho.
            console.error('Erro ao carregar o Tipo de Julgamento', erro);
            Sentry.captureException(erro, { level: 'warning' });
        } finally {
            runInAction(() => {
                ProcessoStore.carregandoTipoJulgamento = false;
            });
        }
    }

    async atualizarPlanilha(): Promise<boolean> {
        try {
            const idsProcesso = EscaninhoStore.processosSelecionados.map(p => p.identificadorProcesso);
            await MinutaApi.atualizarPlanilhaEmLote(idsProcesso);
            MensagemStore.apresentarMensagem("Planilha atualizada");
            return true;
        } catch (e) {
            if (!apresentarApiErroPadrao(e)) {
                MensagemStore.apresentarErro("Erro ao atualizar as minutas na planilha");
            }
            return false;
        }
    }

    async forcaVistoLiberado(): Promise<boolean> {
        const procSelec = EscaninhoStore.processosSelecionados;
        let podeVistoJaRealizado = true;
        procSelec.forEach(proc => {
            if (proc.tarefaFluxoPje?.localizacao?.startsWith('SE') || tarefasParaVistoJaRealizado.includes(proc.tarefaFluxoPje.tarefaAtual.toLowerCase())) {
                podeVistoJaRealizado = true;
            } else {
                podeVistoJaRealizado = false;
            }
        });

        if (podeVistoJaRealizado) {
            const sucesso = [];
            const erro = [];
            for (const processo of procSelec) {
                try {
                    const resultado = await MinutaApi.forcaVistoLiberado(processo.identificadorProcesso, UsuarioStore.gabinete);
                    sucesso.push({ idProcesso: processo.identificadorProcesso, resultadoOperacao: resultado });
                } catch (error) {
                    console.error(erro);
                    erro.push({
                        idProcesso: processo.identificadorProcesso,
                        resultadoOperacao: error,
                        processo: processo
                    });
                }
            };

            const mensagemSucesso = 'Operação realizada com sucesso';
            const mensagemErroParcial = 'Operação não realiza para alguns processos';
            const mensagemErroTotal = 'Erro ao atualizar situação do visto para liberado';

            let mensagem = erro.length <= 0 ? mensagemSucesso : sucesso.length <= 0 ? mensagemErroTotal : mensagemErroParcial;

            for (const i in erro) {
                if (erro[i].resultadoOperacao?.toString().includes('Resposta de erro ao gravar a minuta no PJE')) {
                    mensagem += ' - Ocorreu um erro ao gravar a minuta no PJe no processo ' + erro[i].processo.numeroProcesso + '. Verifique a formação da minuta, pois é uma provável causa.';
                } else if (erro[i].resultadoOperacao?.toString().includes('Processo já está incluído em uma pauta')) {
                    mensagem += 'Favor entrar em contato com a Secretaria para fecharem a última sessão deste processo e tentar novamente. ';
                }
            }

            if (erro.length > 0) {
                MensagemStore.apresentarAviso(mensagem);
            } else {
                MensagemStore.apresentarMensagem(mensagem);
            }

            return true;
        } else {
            MensagemStore.apresentarAviso(`Algum processo selecionado não parece ter sido enviado à pauta. Favor verificar a tarefa/andamento do processo na origem e tentar novamente.`);
        }
    }
}
export default new MovimentacaoInternaService();
